import React, { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { CheckCircle, Close, Download, ErrorOutline } from '@mui/icons-material'
import { useSSE, SSEEvent } from 'contexts/SSEContext'

interface BulkPDFDownloadDialogProps {
  open: boolean
  onClose: () => void
  requestId: string | null
  rewardCount: number
}

type DialogState = 'processing' | 'completed' | 'error'

const BulkPDFDownloadDialog: React.FC<BulkPDFDownloadDialogProps> = ({ open, onClose, requestId, rewardCount }) => {
  const { subscribe } = useSSE()
  const [state, setState] = useState<DialogState>('processing')
  const [progress, setProgress] = useState<{ current: number; total: number }>({ current: 0, total: rewardCount })
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [zipSize, setZipSize] = useState<number | null>(null)

  const progressPercent = progress.total > 0 ? (progress.current / progress.total) * 100 : 0

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Handle progress event
  const handleProgressEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setProgress({
          current: event.data.processedPdfs || 0,
          total: event.data.totalPdfs || rewardCount,
        })
      }
    },
    [requestId, rewardCount]
  )

  // Handle ready event
  const handleReadyEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setState('completed')
        setDownloadUrl(event.data.downloadUrl || null)
        setZipSize(event.data.zipSize || null)
        setProgress({
          current: event.data.totalPdfs || rewardCount,
          total: event.data.totalPdfs || rewardCount,
        })
      }
    },
    [requestId, rewardCount]
  )

  // Handle error event
  const handleErrorEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setState('error')
        setErrorMessage(event.data.error || 'An unknown error occurred')
      }
    },
    [requestId]
  )

  // Subscribe to SSE events
  useEffect(() => {
    if (!open || !requestId) return

    const unsubscribeProgress = subscribe('bulk_pdf_progress', handleProgressEvent)
    const unsubscribeReady = subscribe('bulk_pdf_ready', handleReadyEvent)
    const unsubscribeError = subscribe('bulk_pdf_error', handleErrorEvent)

    return () => {
      unsubscribeProgress()
      unsubscribeReady()
      unsubscribeError()
    }
  }, [open, requestId, subscribe, handleProgressEvent, handleReadyEvent, handleErrorEvent])

  // Handle download
  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      // Open the presigned URL - S3 response-content-disposition header will trigger download
      window.open(downloadUrl, '_blank')
      onClose()
    }
  }, [downloadUrl, onClose])

  // Handle close
  const handleClose = useCallback(() => {
    if (state === 'processing') return
    onClose()
    setTimeout(() => {
      setState('processing')
      setProgress({ current: 0, total: rewardCount })
      setDownloadUrl(null)
      setErrorMessage(null)
      setZipSize(null)
    }, 300)
  }, [state, onClose, rewardCount])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={state === 'processing'}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 280,
        },
      }}
    >
      {/* Close button - only enabled when not processing */}
      {state !== 'processing' && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
      )}

      <DialogContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
        {/* PROCESSING STATE */}
        {state === 'processing' && (
          <Box>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Generating PDFs
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {progress.current} of {progress.total} completed
            </Typography>

            <Box sx={{ px: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* COMPLETED STATE */}
        {state === 'completed' && (
          <Box>
            <CheckCircle
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to Download
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {progress.total} {progress.total === 1 ? 'PDF' : 'PDFs'} packaged into a zip file
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={<Download />}
              onClick={handleDownload}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Download
            </Button>

            {zipSize && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {formatFileSize(zipSize)}
              </Typography>
            )}
          </Box>
        )}

        {/* ERROR STATE */}
        {state === 'error' && (
          <Box>
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Generation Failed
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage || 'An error occurred while generating the PDFs'}
            </Typography>

            <Button variant="contained" onClick={handleClose} fullWidth sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BulkPDFDownloadDialog
